import { memo } from 'react';
import { format } from 'date-fns';
import { useTracking } from 'react-tracking';

import { RouterLink } from '@redux-first-router-link';

import { Grid, Box, Heading, Text, useTheme } from '@simplywallst/ui-core';
import { ChevronRightSmallSolid } from '@simplywallst/ui-core/icons';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { useFlag } from '@/features/unleash';

import { ROUTE_MARKET_HUB } from '@/constants/routes';

import { useIsMobileScreen } from '@/hooks/useMedia';
import {
  getMarketInsightsQueryKey,
  useLatestArticles,
} from '@/hooks/useLatestArticles';

import { ArticleSummary, ArticleFeedItem } from '@/components/ArticleSummary';

import { RecentInsightsSkelecton } from './Skeleton';

import { SidebarContainer, Wrapper } from './styled';

const MemoArticleSummary = memo(ArticleSummary);

const RecentArticles = () => {
  const theme = useTheme();

  const featureFlag = useFlag({ name: 'mono-dashboard-portfolio-widget' });
  const isNewPortfolioSectionEnabled =
    featureFlag.flagsReady && featureFlag.enabled;

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { data, isFetching } = useLatestArticles(getMarketInsightsQueryKey());
  const [highlight = null, secondaryArticle = null] = data?.articles ?? [];
  const { trackEvent } = useTracking();

  if (!highlight) {
    return null;
  }

  return (
    <Wrapper>
      <Box
        position="relative"
        marginBottom="x3"
        paddingBottom="x2"
        paddingTop="x2"
        marginBottomMd="x0"
        paddingBottomMd="x0"
        paddingTopMd="x0"
        backgroundColor={isMobileScreen ? 'noir' : 'transparent'}
      >
        {!isNewPortfolioSectionEnabled ? (
          <Box
            marginLeft="x2"
            marginBottom="x2"
            marginLeftMd="x0"
            marginTopMd="x1"
            marginBottomMd="x1"
          >
            <Heading
              as="h2"
              typography={isMobileScreen ? 'largeStrong' : 'baseStrong'}
              data-cy-id="market-insights"
            >
              Market Insights
            </Heading>
          </Box>
        ) : null}
        <SidebarContainer>
          <Grid paddingRight="x2" paddingLeft="x2" paddingMd="x0">
            {isFetching ? (
              <RecentInsightsSkelecton />
            ) : (
              <>
                <MemoArticleSummary
                  slug={highlight.slug}
                  title={highlight.title}
                  excerpt={highlight.excerpt}
                  imageUrl={highlight.coverURL}
                  publishedDate={format(
                    new Date(highlight.created),
                    'MMMM yyyy'
                  )}
                  author={highlight.author.name}
                  authorAvatarUrl={highlight.author.photoURL}
                  authorSlug={highlight.author.slug}
                />
                {secondaryArticle && (
                  <ArticleFeedItem
                    title={secondaryArticle.title}
                    imageUrl={secondaryArticle.coverURL}
                    slug={secondaryArticle.slug}
                  />
                )}
                {!isNewPortfolioSectionEnabled ? (
                  <Grid
                    templateColumns="auto 20px"
                    alignItems="center"
                    gap="4px"
                  >
                    <Grid placeContent="end">
                      <RouterLink
                        noUnderline
                        data-cy-id="market-hub-more-link"
                        to={{ type: ROUTE_MARKET_HUB }}
                        onClick={() => {
                          trackEvent({
                            type: 'track',
                            subject: 'more-market-insights',
                            action: 'click',
                          });
                        }}
                      >
                        <Text typography="xSmall" color="brand01">
                          More Market Insights
                        </Text>
                      </RouterLink>
                    </Grid>
                    <Grid placeContent="end">
                      <ChevronRightSmallSolid
                        className="icon"
                        fill={theme.color.brand01}
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>
        </SidebarContainer>
      </Box>
    </Wrapper>
  );
};

export default RecentArticles;
